import { USE_TRANSCRIPTION_SERVICE_V2 } from '@/app-shell/flags/capture-flags';
import { isChunkedLiveDictation } from '@/app-shell/flags/live-dictate-transcript-flag';
import { useCurrentInputLanguages } from '@/hooks/useCurrentInputLanguages';
import { useRecordingV2 } from '@/hooks/useRecording/useRecordingV2';
import { linearChunkDurations } from '@/lib/audio/utils';
import { beginRecordingFinalizeAttempt } from '@/lib/telemetry/recordingFinalizeTracking';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { scribeHttp } from '@/utils/scribeHTTP';
import { type BackupToUpload, type SessionChunk } from '@heidi/capture/contracts';
import { getCodec } from '@heidi/capture/getCodec';
import { getFormat } from '@heidi/capture/getFormat';
import { type SessionRecordingConfig } from '@heidi/capture/ports';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { useMutateUpdateSessionRecordingStatus } from '@heidi/session/data';
import {
  memo,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  prepareWriteFenceForHydrate,
  useSessionRecordingStore,
} from '../audio/useSessionRecordingStore/useSessionRecordingStore';
import { prepareChunkUploadV3 } from './chunkUploadV3';
import { getRecordingId } from './getRecordingId';
import { getTranscriptionBaseUrl, t11nClient } from './t11nClient';
import { uploadBackup } from './uploadBackup';
import { uploadChunk } from './uploadChunk';
import { useFinishChunkRecording } from './useFinishChunkRecording';
import { useOnError } from './useOnError';
import { useSessionAudioLevelLogging } from './useSessionAudioLevelLogging';

// Whisper transcribes a chunk this short in about a second, so the transcript
// trails speech by one chunk. Ambient keeps the longer schedule: nobody reads
// it live, and longer audio transcribes more accurately.
const liveDictationChunkDurations = [{ minDuration: 5000, maxDuration: 10000 }];

const getChunkDurations = (sessionId: string) => {
  const session = reactQueryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);
  return isChunkedLiveDictation(session?.transcribe_mode)
    ? liveDictationChunkDurations
    : linearChunkDurations;
};

export const SessionRecordingProvider = (props: PropsWithChildren) => {
  const setSessionId = useSessionRecordingStore((s) => s.setSessionId);
  const setConfig = useSessionRecordingStore((s) => s.setConfig);
  const hydrate = useSessionRecordingStore((s) => s.hydrate);
  const stop = useSessionRecordingStore((s) => s.stop);
  // Liveness is the recording id, not status: a failed chunk flips status to
  // ERROR while capture continues.
  const liveRecordingId = useSessionRecordingStore((s) => s.recordingId);
  const hydrateParked = useRef(false);
  const sessionId = useCurrentSessionId();
  const finish = useFinishChunkRecording(sessionId);
  const updateRecordingStatus = useMutateUpdateSessionRecordingStatus();
  const { onInterrupt: recordingOnInterrupt } = useRecordingV2();
  const format = getFormat();
  const codec = getCodec(format);
  const languages = useCurrentInputLanguages();

  const onInterrupt = useCallback(() => {
    void stop();
    updateRecordingStatus.mutate({
      recordingStatus: RecordingStatus.PAUSED,
      sessionId,
      onMutate: () => recordingOnInterrupt(),
    });
  }, [stop, updateRecordingStatus, sessionId, recordingOnInterrupt]);

  const onError = useOnError({ sessionId, onInterrupt });

  // The recording is running but not saved on this device. Design owns the
  // surface for that warning; until it ships, the port is received and
  // deliberately not shown. Not `onError`: that copy says the start failed.
  const onStorageDegraded = useCallback(() => {}, []);

  const config: SessionRecordingConfig = useMemo(
    () => ({
      getRecordingId: (sessionId, useTranscriptionServiceV2) =>
        getRecordingId(sessionId, useTranscriptionServiceV2),
      uploadChunk: (
        chunk: SessionChunk,
        useTranscriptionServiceV2,
        signal,
        uploadTransport
      ) =>
        uploadChunk(
          chunk,
          format,
          useTranscriptionServiceV2,
          languages,
          signal,
          uploadTransport
        ),
      prepareUploadTransport: prepareChunkUploadV3,
      languages,
      checkConnectivity: async (signal) => {
        const transcriptionBaseUrl = USE_TRANSCRIPTION_SERVICE_V2()
          ? getTranscriptionBaseUrl()
          : null;
        if (transcriptionBaseUrl) {
          const healthUrl = transcriptionBaseUrl.replace('v1', '') + 'health';
          await (signal
            ? t11nClient.get(healthUrl, { signal })
            : t11nClient.get(healthUrl));
          return;
        }
        await (signal
          ? scribeHttp.get(`/healthcheck`, { signal })
          : scribeHttp.get(`/healthcheck`));
      },
      uploadBackup: (backup: BackupToUpload, signal) =>
        uploadBackup(backup, format, undefined, signal),
      onError,
      onStorageDegraded,
      onStopError: (purpose) => {
        // Compensation has not confirmed capture cessation. Its start adapter
        // owns timer/duration cleanup; ordinary Stop/Pause keeps its callback.
        if (purpose !== 'start-compensation') recordingOnInterrupt();
      },
      // A getter: the engine reads it each time it builds a recorder, so the
      // schedule follows the mode the session is in when recording starts.
      get chunkSpec() {
        return { chunkDurations: getChunkDurations(sessionId) };
      },
      // Capture ended with chunks to deliver: the finalize attempt opens here
      // and `finish` closes it.
      onCaptureEnded: (recordingId) => beginRecordingFinalizeAttempt(recordingId),
      onLastChunkProcessed: finish,
      onInterrupt,
      format,
      codec,
    }),
    [
      onError,
      onStorageDegraded,
      recordingOnInterrupt,
      finish,
      onInterrupt,
      codec,
      format,
      languages,
      sessionId,
    ]
  );

  useEffect(() => {
    setConfig(config);
  }, [config, setConfig]);

  useEffect(() => {
    setSessionId(sessionId);
  }, [sessionId, setSessionId]);

  const runHydrateGate = useCallback(() => {
    if (isTauri()) {
      void hydrate(sessionId);
      return;
    }
    // Recover incomplete logout wipes before hydration.
    void prepareWriteFenceForHydrate().then((prepared) => {
      if (prepared === 'ready') void hydrate(sessionId);
    });
  }, [sessionId, hydrate]);

  useEffect(() => {
    // The gate clears wipe suppression and can re-wipe the ledger — never
    // under a live recording. A route change re-runs this effect.
    if (useSessionRecordingStore.getState().recordingId !== null) {
      hydrateParked.current = true;
      logger.info({
        event_type: 'HYDRATE_SKIPPED_WHILE_RECORDING',
        details: { sessionId },
      });
      return;
    }
    hydrateParked.current = false;
    runHydrateGate();
  }, [sessionId, runHydrateGate]);

  useEffect(() => {
    // Deferred, not dropped: runs once when the recording clears, never for a
    // change that parked nothing.
    if (liveRecordingId !== null || !hydrateParked.current) return;
    hydrateParked.current = false;
    runHydrateGate();
  }, [liveRecordingId, runHydrateGate]);

  return <LoggingComponentMemo>{props.children}</LoggingComponentMemo>;
};

const LoggingComponentMemo = memo(function LoggingComponent(props: PropsWithChildren) {
  useSessionAudioLevelLogging();
  return props.children;
});
